import { Editor } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import {
  ACCEPTED_IMAGE_MIME_TYPES,
  validateImageFile,
  validateImageUrl
} from "./image/image-helpers";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

type DropHandlerPluginProps = {
  editor: Editor;
  uploadImage?: (file: File) => Promise<string>;
};

export const DropHandlerPlugin = (props: DropHandlerPluginProps): Plugin => {
  const { editor, uploadImage } = props;

  return new Plugin({
    key: new PluginKey("drop-handler-plugin"),
    props: {
      handleDOMEvents: {
        dragover: (view, event) => {
          if (editor.isEditable) {
            event.preventDefault();
            event.dataTransfer!.dropEffect = "copy";
            return true;
          }
          return false;
        },
        drop: () => {
          return false;
        }
      },
      handlePaste: (view, event) => {
        if (!editor.isEditable || !event.clipboardData) return false;

        if (event.clipboardData.files.length) {
          const files = Array.from(event.clipboardData.files);
          const acceptedFiles = files.filter((f) =>
            ACCEPTED_IMAGE_MIME_TYPES.includes(f.type)
          );

          if (acceptedFiles.length) {
            event.preventDefault();
            const pos = view.state.selection.from;

            insertImageFiles({
              editor,
              files: acceptedFiles,
              pos,
              uploadImage
            });

            return true;
          }
        }

        const text = (event.clipboardData.getData("text/plain") || "").trim();
        if (text.length > 0)
          handleImageUrlPaste({
            editor,
            url: text,
            view
          });

        return false;
      },

      handleDrop: (view, event, _slice, moved) => {
        if (editor.isEditable && !moved && event.dataTransfer?.files.length) {
          const files = Array.from(event.dataTransfer.files);
          const acceptedFiles = files.filter((f) =>
            ACCEPTED_IMAGE_MIME_TYPES.includes(f.type)
          );

          if (acceptedFiles.length) {
            event.preventDefault();

            const coordinates = view.posAtCoords({
              left: event.clientX,
              top: event.clientY
            });

            if (coordinates) {
              insertImageFiles({
                editor,
                files: acceptedFiles,
                pos: coordinates.pos,
                uploadImage
              });

              return true;
            }
          }
        }
        return false;
      }
    }
  });
};

type InsertImageFilesArgs = {
  editor: Editor;
  files: File[];
  pos: number;
  uploadImage?: (file: File) => Promise<string>;
};

const insertImageFiles = async (args: InsertImageFilesArgs) => {
  const { editor, files, pos, uploadImage } = args;
  let currentPos = pos;

  for (const file of files) {
    const docSize = editor.state.doc.content.size;
    currentPos = Math.min(currentPos, docSize);

    const validationError = validateImageFile(file);

    if (validationError) {
      toast.error(validationError.message);
      continue;
    }

    try {
      if (uploadImage) {
        editor.storage.utility.uploadInProgress = true;

        try {
          const url = await uploadImage(file);

          editor
            .chain()
            .insertContentAt(currentPos, {
              type: "paragraph",
              content: [
                {
                  type: "text",
                  text: `[${file.name}](${url})`
                }
              ]
            })
            .run();

          currentPos += 1;
        } catch (error) {
          const message = getErrorMessage(error);
          toast.error(message);
          editor.storage.utility.uploadInProgress = false;
        }
      } else {
        editor
          .chain()
          .insertContentAt(currentPos, {
            type: "paragraph",
            content: [
              {
                type: "text",
                text: file.name
              }
            ]
          })
          .run();

        currentPos += 1;
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
    }
  }
};

type HandleImageUrlPasteArgs = {
  editor: Editor;
  url: string;
  view: any;
};

const handleImageUrlPaste = async (args: HandleImageUrlPasteArgs) => {
  const { editor, url, view } = args;

  const isValid = await validateImageUrl(url);

  if (!isValid) return;

  const pos = view.state.selection.from;
  editor
    .chain()
    .insertContentAt(pos, {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: `[Image URL](${url})`
        }
      ]
    })
    .run();
};
